// @givmo/design-system — PublicProfileHero.
//
// The header every Givmo PUBLIC surface shows for an organization: a large circular logo (initials
// when there is none), the display name, an optional line under it (a location), an optional
// two-line description, and whatever the caller puts after it (a website line). Extracted verbatim
// from the donate app's target hero (`apps/donate` `EntityHero`, which now composes it) so the
// organization console's "View as public" tab renders the SAME header a visitor sees — one
// presentation, two callers, never a copy (Profile Enrichment D-PE-17 (6)).
//
// The caller owns the copy: `description` renders as given (the donate hero passes a word-boundary
// excerpt) and is clamped to two lines by CSS. A blank/whitespace `imageUrl` is treated as absent,
// so a server render never emits an <img> for nothing; a URL that fails later swaps to the initials
// through Avatar's own handling.
//
// A charity's header also carries "Show more" (`more`; CPA-BE44, design 20 §11, the v3 mockup
// approved in D-CP-98): under the excerpt, closed on first paint, it opens to the full mission in
// place and then the charity's detail rows (Legal name, EIN, Category, Website), and reads "Show
// less". It lives here, not in either caller, so the donate page and the console's "View as
// public" draw the same control over the same facts. The toggle is `Disclosure`, the native
// `<details>`, so the excerpt-to-mission swap is CSS keyed on it (`WHILE_DISCLOSURE_CLOSED` /
// `_OPEN`) and works before hydration.
import type { ReactNode } from 'react';
import { Avatar } from './avatar';
import {
  DISCLOSURE_HOST,
  Disclosure,
  WHILE_DISCLOSURE_CLOSED,
  WHILE_DISCLOSURE_OPEN,
} from './disclosure';
import { cn } from '../lib/utils';
import { testIdProp, type TestIdProps } from '../lib/testid';

/** The mission / description excerpt the public hero shows under the name (the donate page's cap). */
export const PUBLIC_PROFILE_DESCRIPTION_EXCERPT_MAX = 72;

const SIGNIFICANT_WORD_PATTERN = /[\p{L}\p{N}]+/gu;
const NEUTRAL_INITIALS = '?';

/**
 * The public-hero initials: the first letter of the first three words, ignoring punctuation and
 * whitespace (Flutter parity — the donate page's rule since day one). Unexpected or empty input
 * degrades to a neutral glyph so a malformed display name can never break a public header.
 */
export function publicProfileInitials(name: string | null | undefined): string {
  if (typeof name !== 'string') return NEUTRAL_INITIALS;
  const words = name.match(SIGNIFICANT_WORD_PATTERN) ?? [];
  const initials = words
    .slice(0, 3)
    .map((word) => Array.from(word)[0]?.toUpperCase() ?? '')
    .join('');
  return initials || NEUTRAL_INITIALS;
}

/**
 * The public-hero description excerpt: whitespace flattened, cut at the last word boundary before
 * `max` characters with ONE explicit ellipsis (the donate page's rule). `null` in, `null` out; a
 * blank body is `null` too.
 */
export function publicProfileExcerpt(text: string | null | undefined, max: number): string | null {
  if (typeof text !== 'string') return null;
  const flat = text.replace(/\s+/g, ' ').trim();
  if (flat.length === 0) return null;
  if (flat.length <= max) return flat;
  const slice = flat.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  const cut = lastSpace > 0 ? slice.slice(0, lastSpace) : slice;
  return `${cut.trimEnd()}…`;
}

/** The facts a charity's "Show more" reveals under its full mission, each `null` when absent. */
export interface PublicProfileDetails {
  /** The name the IRS records for the charity's EIN, verbatim. */
  legalName: string | null;
  /** `XX-XXXXXXX`. */
  ein: string | null;
  category: string | null;
  /** `https://{organization_domain}`. */
  website: string | null;
}

function trimmedOrNull(value: string | null | undefined): string | null {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  return trimmed.length > 0 ? trimmed : null;
}

/** An EIN as `XX-XXXXXXX`; a value that is not nine digits is shown as given. */
export function formatPublicProfileEin(value: string | null | undefined): string | null {
  const raw = trimmedOrNull(value);
  if (raw === null) return null;
  const digits = raw.replace(/\D/g, '');
  return digits.length === 9 ? `${digits.slice(0, 2)}-${digits.slice(2)}` : raw;
}

/**
 * The detail rows from a charity read's own fields (`GET /charities/{id}`: `legal_name`, `EIN`,
 * `category`, `organization_domain`); `null` when it has none of them.
 */
export function publicProfileDetails(facts: {
  legalName?: string | null;
  ein?: string | null;
  category?: string | null;
  organizationDomain?: string | null;
}): PublicProfileDetails | null {
  const domain = trimmedOrNull(facts.organizationDomain);
  const details: PublicProfileDetails = {
    legalName: trimmedOrNull(facts.legalName),
    ein: formatPublicProfileEin(facts.ein),
    category: trimmedOrNull(facts.category),
    website: domain === null ? null : `https://${domain}`,
  };
  return Object.values(details).some((value) => value !== null) ? details : null;
}

/** What "Show more" opens to: the full mission, then the detail rows. */
export interface PublicProfileMore {
  /** The full mission; `null` when there is none (the toggle then opens to the rows alone). */
  mission: string | null;
  details: PublicProfileDetails | null;
  /** The toggle's id; `${testId}-more-toggle` by default. */
  toggleTestId?: string;
  /** The rows' id (each row is `${id}-<row>`); `${testId}-details` by default. */
  detailsTestId?: string;
}

interface DetailRow {
  key: 'legal-name' | 'ein' | 'category' | 'website';
  label: string;
  value: string;
  href?: string;
}

function detailRowsOf(details: PublicProfileDetails): DetailRow[] {
  const rows: DetailRow[] = [];
  if (details.legalName)
    rows.push({ key: 'legal-name', label: 'Legal name', value: details.legalName });
  if (details.ein) rows.push({ key: 'ein', label: 'EIN', value: details.ein });
  if (details.category) rows.push({ key: 'category', label: 'Category', value: details.category });
  if (details.website) {
    rows.push({
      key: 'website',
      label: 'Website',
      value: details.website.replace(/^https:\/\//, ''),
      href: details.website,
    });
  }
  return rows;
}

/**
 * The detail rows, in this order, each left out when absent: Legal name (verbatim, as the IRS
 * records it), EIN, Category, Website (the bare domain, opening `https://{domain}` in a new tab).
 * Nothing here is editable. A hairline above the rows sets them apart from the toggle.
 */
function PublicProfileDetailRows({
  details,
  testId,
}: {
  details: PublicProfileDetails;
  testId?: string;
}) {
  return (
    <dl
      className="flex w-full flex-col gap-10 border-t border-border pt-14 text-left mt-4 mb-4"
      {...testIdProp(testId)}
    >
      {detailRowsOf(details).map((row) => (
        <div
          key={row.key}
          className="flex items-baseline justify-between gap-16"
          {...testIdProp(testId ? `${testId}-${row.key}` : undefined)}
        >
          <dt className="shrink-0 text-s14 font-w400 tracking-normal text-gray-text">
            {row.label}
          </dt>
          <dd className="min-w-0 break-words text-right text-balance text-s14 font-w500 tracking-normal text-gray80">
            {row.href ? (
              <a
                href={row.href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue underline-offset-2 hover:underline"
              >
                {row.value}
              </a>
            ) : (
              row.value
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export interface PublicProfileHeroProps extends TestIdProps {
  name: string;
  /** The logo. Blank or whitespace is absent. */
  imageUrl?: string | null;
  /** Caller-derived initials for the no-image case; defaults to Avatar's rule. */
  fallbackText?: string;
  /** One short line directly under the name — a location. Blank is absent. */
  subline?: string | null;
  /** Up to two lines under the name (already excerpted by the caller); clamped to two lines. */
  description?: string | null;
  /**
   * A charity's "Show more": shown whenever there is more to show, a mission longer than the
   * excerpt or any detail row. Open, the full mission replaces the excerpt and the rows follow.
   */
  more?: PublicProfileMore;
  /**
   * The heading level of the name. The donate page's hero IS the page's heading (1); a surface
   * that already carries an `h1` — a console page previewing the public page — passes 2.
   */
  headingLevel?: 1 | 2;
  /** Anything the surface shows after the description — a website line. */
  children?: ReactNode;
  className?: string;
}

export function PublicProfileHero({
  name,
  imageUrl,
  fallbackText,
  subline,
  description,
  more,
  headingLevel = 1,
  children,
  className,
  testId,
}: PublicProfileHeroProps) {
  const src = imageUrl?.trim() || null;
  const sub = subline?.trim() || null;
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const mission = more?.mission?.trim() || null;
  const details = more?.details ?? null;
  // The excerpt flattens whitespace, so a mission is "more" only when its words run past it.
  const missionRunsOn = mission !== null && mission.replace(/\s+/g, ' ') !== (description ?? null);
  const hasMore = more !== undefined && (details !== null || missionRunsOn);
  const toggleTestId = more?.toggleTestId ?? (testId ? `${testId}-more-toggle` : undefined);
  const detailsTestId = more?.detailsTestId ?? (testId ? `${testId}-details` : undefined);
  return (
    <section
      className={cn(
        'flex w-full flex-col items-center text-center',
        hasMore && DISCLOSURE_HOST,
        className,
      )}
      {...testIdProp(testId)}
    >
      {/* The letters' padding rides on the initials alone: on the 77px box it shrank an image logo
          to 65px wide and showed the #3A3A3A fill beside it (PE-WEB14). A logo fills its circle,
          as the apps draw it; the initials render exactly as before. */}
      <Avatar
        src={src}
        name={name}
        {...(fallbackText === undefined ? {} : { fallbackText })}
        size="lg"
        className="size-[77px] rounded-full bg-[#3A3A3A] text-s28 font-w700 leading-none tracking-normal text-white [&_img]:object-top"
        initialsClassName="px-6"
        testId={testId ? `${testId}-image` : undefined}
      />

      <Heading
        className="mt-12 text-s24 font-w700 leading-tight tracking-normal text-black"
        {...testIdProp(testId ? `${testId}-name` : undefined)}
      >
        {name}
      </Heading>

      {sub !== null && (
        <p
          className="mt-2 text-s14 font-w400 leading-snug tracking-normal text-gray60"
          {...testIdProp(testId ? `${testId}-subline` : undefined)}
        >
          {sub}
        </p>
      )}

      {description ? (
        <p
          className={cn(
            'line-clamp-2 text-s16 font-w400 leading-snug tracking-normal text-gray60',
            hasMore && WHILE_DISCLOSURE_CLOSED,
          )}
          {...testIdProp(testId ? `${testId}-description` : undefined)}
        >
          {description}
        </p>
      ) : null}

      {hasMore ? (
        <>
          {/* The full mission is body copy, so it reads as the page's Profile story body does
              (left-aligned, dark), in the same column as the rows under it. */}
          {mission !== null ? (
            <p
              className={cn(
                WHILE_DISCLOSURE_OPEN,
                'mt-4 w-full max-w-[345px] whitespace-pre-line text-left text-s15 font-w500 leading-[1.46] tracking-normal text-gray80',
              )}
              {...testIdProp(testId ? `${testId}-mission` : undefined)}
            >
              {mission}
            </p>
          ) : null}
          <Disclosure
            label={
              <>
                Show more<span className="sr-only"> about {name}</span>
              </>
            }
            openLabel={
              <>
                Show less<span className="sr-only"> about {name}</span>
              </>
            }
            className="mt-4 w-full max-w-[345px]"
            summaryClassName="mx-auto"
            {...(toggleTestId === undefined ? {} : { testId: toggleTestId })}
          >
            {details !== null ? (
              <PublicProfileDetailRows
                details={details}
                {...(detailsTestId === undefined ? {} : { testId: detailsTestId })}
              />
            ) : null}
          </Disclosure>
        </>
      ) : null}

      {children}
    </section>
  );
}
